import type {
  Organization,
  PharmacyDelivery,
  PharmacyPublishedPrices,
} from "./providers.types";

export type PharmacyDirectoryType = "specialty" | "mail_order" | "retail";
export type PharmacyDirectorySource = "live" | "curated" | "demo";

export type PharmacyDemoMedication = {
  id: string;
  name: string;
  dosage: string;
  form: "pen" | "vial" | "syringe" | "tablet" | "suppository";
  route: string;
  coverage: "covered" | "cash_pay";
};

export type PharmacyDemoMedicationPrice = {
  medicationId: string;
  price: number | null;
};

export type PharmacyDemoProfile = {
  acceptsInsurance: boolean | null;
  delivery: string;
  totalPrice: number | null;
  medicationPrices?: PharmacyDemoMedicationPrice[];
};

export type PharmacyDemoQuote = {
  pharmacyId: string;
  price: number | null;
  estimatedSavings?: number;
};

export type PharmacySavingsCoupon = {
  medicationName: string;
  bin: string;
  pcn: string;
  group: string;
  memberId: string;
  sourceUrl: string;
};

export type PharmacySavingsProgramLocalizedFields = {
  name?: string;
  sponsor?: string;
  headline?: string;
  processLabel?: string;
  medicationLabel?: string;
  savingsLabel?: string;
  eligibilityLabel?: string;
};

export type PharmacySavingsProgram = {
  id: string;
  name: string;
  sponsor: string;
  headline: string;
  processLabel: string;
  eligibleMedIds: string[];
  medicationLabel: string;
  savingsLabel: string;
  eligibilityLabel: string;
  sourceUrl: string;
  lastVerifiedOn: string;
  coupon?: PharmacySavingsCoupon;
  translations?: {
    fr?: PharmacySavingsProgramLocalizedFields;
  };
};

export function localizePharmacySavingsProgram(
  program: PharmacySavingsProgram,
  locale: "en" | "fr",
): PharmacySavingsProgram {
  if (locale === "en") return program;
  const overrides = program.translations?.[locale];
  if (!overrides) return program;
  return {
    ...program,
    name: overrides.name ?? program.name,
    sponsor: overrides.sponsor ?? program.sponsor,
    headline: overrides.headline ?? program.headline,
    processLabel: overrides.processLabel ?? program.processLabel,
    medicationLabel: overrides.medicationLabel ?? program.medicationLabel,
    savingsLabel: overrides.savingsLabel ?? program.savingsLabel,
    eligibilityLabel: overrides.eligibilityLabel ?? program.eligibilityLabel,
  };
}

/**
 * How the pharmacy takes new fills:
 * - `quote-form`  — publishes a direct fertility price-quote form
 * - `onboarding`  — routes new patients through a portal / enrollment flow
 * - `insurance`   — dispenses via the patient's insurance benefit; users
 *                   must enroll or check coverage rather than request a quote
 */
export type PharmacyQuoteType = "quote-form" | "onboarding" | "insurance";

export type PharmacyDirectoryRecord = {
  id: string;
  name: string;
  type: PharmacyDirectoryType;
  website?: string;
  phone?: string;
  city?: string;
  state?: string;
  country?: string;
  specialties: string[];
  description?: string;
  source: PharmacyDirectorySource;
  sourceUrl?: string;
  quoteUrl?: string;
  medicationsUrl?: string;
  quoteType?: PharmacyQuoteType;
  compoundingOnSite?: boolean;
  delivery?: PharmacyDelivery;
  publishedPrices?: PharmacyPublishedPrices;
  organization?: Organization;
  demo?: PharmacyDemoProfile;
};

type VerifiedContact = {
  website?: string;
  phone?: string;
  sourceUrl: string;
  quoteUrl?: string;
  medicationsUrl?: string;
  quoteType?: PharmacyQuoteType;
};

/**
 * Contact values checked against the provider's own site.
 * Original batch verified 2026-07-26; Metro Drugs, Apthorp, and Rosemont
 * verified 2026-08-24.
 * Curated records intentionally do not expose an unverified phone number.
 * API-backed records use the current provider-directory response instead.
 */
const VERIFIED_CONTACTS: Record<string, VerifiedContact> = {
  "org-pharmacy-alto": {
    website: "https://www.alto.com",
    phone: "1 (800) 874-5881",
    sourceUrl: "https://www.alto.com/contact",
    quoteUrl: "https://app.alto.com/patient-onboarding",
    quoteType: "onboarding",
  },
  "org-pharmacy-mdr": {
    website: "https://www.mdrusa.com",
    phone: "1 (800) 515-3784",
    sourceUrl: "https://www.mdrusa.com/",
    quoteUrl: "https://pricequote.mdrusa.com/patient/quote-pricing.php",
    quoteType: "quote-form",
  },
  "org-pharmacy-freedom": {
    website: "https://freedomfertility.com",
    phone: "1 (800) 660-4283",
    sourceUrl: "https://freedomfertility.com/",
    quoteUrl: "https://patients.freedomfertility.com/order-refills/",
    quoteType: "onboarding",
  },
  "org-pharmacy-vfp": {
    website: "https://www.vfppharmacygroup.com",
    phone: "1 (855) 306-4888",
    sourceUrl: "https://www.vfppharmacygroup.com/contact-us",
    quoteUrl: "https://www.vfppharmacygroup.com/contact-us",
    quoteType: "quote-form",
  },
  "org-pharmacy-mandells": {
    website: "https://mymandellspharmacy.com",
    phone: "1 (877) 252-0553",
    sourceUrl:
      "https://www.mandellsclinicalpharmacy.com/frequently-asked-questions/",
    quoteUrl: "https://www.mymandellspharmacy.com/order/",
    medicationsUrl: "https://www.mymandellspharmacy.com/drug-pricing/",
    quoteType: "quote-form",
  },
  "org-pharmacy-smp": {
    website: "https://smppharmacy.com",
    phone: "1 (855) 255-5005",
    sourceUrl: "https://smppharmacy.com/our-locations/",
    quoteUrl: "https://smppharmacy.com/contact-us/",
    medicationsUrl: "https://smppharmacy.com/fertility-medications/",
    quoteType: "quote-form",
  },
  "org-pharmacy-apri": {
    website: "https://www.aprinow.com",
    phone: "1 (858) 771-7791",
    sourceUrl: "https://www.aprinow.com/about-us.html",
    quoteUrl: "https://www.aprinow.com/get-a-price-quote.html",
    quoteType: "quote-form",
  },
  "org-pharmacy-prima": {
    website: "https://www.forprima.com",
    phone: "1 (718) 230-3535",
    sourceUrl: "https://www.forprima.com/pages/get-started",
    quoteUrl: "https://www.forprima.com/pages/fertility-price-quote",
    medicationsUrl:
      "https://www.forprima.com/collections/fertility-medications",
    quoteType: "quote-form",
  },
  "org-pharmacy-cvs-specialty": {
    website: "https://www.cvsspecialty.com",
    phone: "1 (800) 237-2767",
    sourceUrl:
      "https://www.cvsspecialty.com/find-resources/conditions/infertility.html",
    quoteUrl:
      "https://www.cvsspecialty.com/content/dam/enterprise/specialty/enrollment_forms/us/US_Fertility.pdf",
    quoteType: "insurance",
  },
  "org-pharmacy-walgreens-specialty": {
    website: "https://www.walgreens.com/topic/pharmacy/specialty-pharmacy.jsp",
    phone: "1 (888) 782-8443",
    sourceUrl:
      "https://www.walgreens.com/topic/pharmacy/specialty-pharmacy/fertility-services.jsp",
    quoteUrl:
      "https://www.walgreens.com/topic/pharmacy/specialty-pharmacy/fertility-services.jsp",
    quoteType: "insurance",
  },
  "org-pharmacy-metro-drugs": {
    website: "https://metrodrugs.com",
    phone: "1 (888) 258-0106",
    sourceUrl: "https://metrodrugs.com/",
    quoteUrl: "https://metrodrugs.com/price-quote/",
    medicationsUrl:
      "https://metrodrugs.com/patient-services/medication-information/",
    quoteType: "quote-form",
  },
  "org-pharmacy-apthorp": {
    website: "https://apthorprx.com",
    phone: "1 (800) 775-3582",
    sourceUrl: "https://apthorprx.com/fertility/",
    quoteUrl: "https://apthorprx.com/your-price-quote/",
    medicationsUrl: "https://apthorprx.com/fertility/fertility-medications/",
    quoteType: "quote-form",
  },
  "org-pharmacy-rosemont": {
    website: "https://rosemontpharmacy.com",
    phone: "1 (610) 525-3570",
    sourceUrl: "https://rosemontpharmacy.com/",
    quoteUrl: "https://rosemontpharmacy.com/medication-price-quote",
    quoteType: "quote-form",
  },
  "org-pharmacy-fuze": {
    website: "https://rx.fuzehealth.com",
    phone: "1 (833) 860-1057",
    sourceUrl: "https://rx.fuzehealth.com/",
    quoteUrl: "https://patients.rx.fuzehealth.com",
    quoteType: "onboarding",
  },
};

/**
 * Demonstration-only enrichment for product walkthroughs. The pharmacy
 * membership mirrors the curated directory; only the medications, prices,
 * ratings, delivery promises, and savings below are illustrative UI content,
 * not pharmacy quotes or eligibility decisions.
 */
export const MOCK_PHARMACY_MEDICATIONS: PharmacyDemoMedication[] = [
  {
    id: "demo-med-gonal-f",
    name: "Gonal-F",
    dosage: "900 IU",
    form: "pen",
    route: "SubQ injection",
    coverage: "covered",
  },
  {
    id: "demo-med-cetrotide",
    name: "Cetrotide",
    dosage: "0.25 mg",
    form: "syringe",
    route: "SubQ injection",
    coverage: "covered",
  },
  {
    id: "demo-med-ovidrel",
    name: "Ovidrel",
    dosage: "250 mcg",
    form: "syringe",
    route: "SubQ injection",
    coverage: "covered",
  },
  {
    id: "demo-med-endometrin",
    name: "Endometrin",
    dosage: "100 mg",
    form: "suppository",
    route: "Vaginal",
    coverage: "cash_pay",
  },
  {
    id: "demo-med-dexamethasone",
    name: "Dexamethasone",
    dosage: "0.5 mg",
    form: "tablet",
    route: "Oral",
    coverage: "covered",
  },
];

const MOCK_PHARMACY_ROWS: {
  id: string;
  name: string;
  type: PharmacyDirectoryType;
  city: string;
  state: string;
  specialties: string[];
  demo: PharmacyDemoProfile;
}[] = [
  {
    id: "org-pharmacy-alto",
    name: "Alto Pharmacy",
    type: "specialty",
    city: "San Francisco",
    state: "CA",
    specialties: ["same-day-delivery", "injection-training", "cold-chain"],
    demo: {
      acceptsInsurance: true,
      delivery: "Free same-day delivery",
      totalPrice: null,
    },
  },
  {
    id: "org-pharmacy-mdr",
    name: "MDR Pharmacy",
    type: "specialty",
    city: "Encino",
    state: "CA",
    specialties: [
      "cash-pay",
      "price-match",
      "injection-training",
      "cold-chain",
    ],
    demo: {
      acceptsInsurance: false,
      delivery: "Free next-day shipping",
      totalPrice: 1955,
    },
  },
  {
    id: "org-pharmacy-freedom",
    name: "Freedom Fertility Pharmacy",
    type: "specialty",
    city: "Newburyport",
    state: "MA",
    specialties: ["overnight-shipping", "cold-chain"],
    demo: {
      acceptsInsurance: true,
      delivery: "Free overnight shipping",
      totalPrice: 1707,
    },
  },
  {
    id: "org-pharmacy-vfp",
    name: "VFP Pharmacy Group",
    type: "specialty",
    city: "Los Angeles",
    state: "CA",
    specialties: ["injection-training", "same-day-delivery", "cold-chain"],
    demo: {
      acceptsInsurance: false,
      delivery: "Free next-day delivery",
      totalPrice: 1707,
    },
  },
  {
    id: "org-pharmacy-apri",
    name: "Apri Pharmacy",
    type: "specialty",
    city: "San Diego",
    state: "CA",
    specialties: ["injection-training", "cold-chain"],
    demo: {
      acceptsInsurance: null,
      delivery: "Free delivery",
      totalPrice: null,
    },
  },
  {
    id: "org-pharmacy-prima",
    name: "Prima Pharmacy",
    type: "specialty",
    city: "New York",
    state: "NY",
    specialties: ["same-day-delivery", "cold-chain"],
    demo: {
      acceptsInsurance: null,
      delivery: "Free delivery",
      totalPrice: null,
    },
  },
  {
    id: "org-pharmacy-mandells",
    name: "Mandell's Pharmacy",
    type: "specialty",
    city: "Somerset",
    state: "NJ",
    specialties: ["injection-training", "automatic-discounts", "cold-chain"],
    demo: {
      acceptsInsurance: true,
      delivery: "Two-day shipping",
      totalPrice: 1956,
    },
  },
  {
    id: "org-pharmacy-smp",
    name: "SMP Pharmacy",
    type: "specialty",
    city: "Northridge",
    state: "CA",
    specialties: ["injection-training", "cold-chain"],
    demo: {
      acceptsInsurance: true,
      delivery: "Free two-day shipping",
      totalPrice: null,
    },
  },
  {
    id: "org-pharmacy-cvs-specialty",
    name: "CVS Specialty",
    type: "retail",
    city: "San Francisco",
    state: "CA",
    specialties: ["retail", "in-store-pickup", "cold-chain"],
    demo: {
      acceptsInsurance: true,
      delivery: "Pickup or three-day shipping",
      totalPrice: 2040,
    },
  },
  {
    id: "org-pharmacy-walgreens-specialty",
    name: "Walgreens Specialty Pharmacy",
    type: "retail",
    city: "Deerfield",
    state: "IL",
    specialties: ["retail", "injection-training", "overnight-delivery"],
    demo: {
      acceptsInsurance: true,
      delivery: "Pickup or overnight shipping",
      totalPrice: 1956,
    },
  },
  {
    id: "org-pharmacy-metro-drugs",
    name: "Metro Drugs",
    type: "specialty",
    city: "New York",
    state: "NY",
    specialties: ["same-day-delivery", "injection-training", "cold-chain"],
    demo: {
      acceptsInsurance: true,
      delivery: "Same-day NYC, overnight nationwide",
      totalPrice: null,
    },
  },
  {
    id: "org-pharmacy-apthorp",
    name: "Apthorp Pharmacy",
    type: "specialty",
    city: "New York",
    state: "NY",
    specialties: ["same-day-delivery", "overnight-shipping", "cold-chain"],
    demo: {
      acceptsInsurance: true,
      delivery: "Same-day Manhattan, overnight nationwide",
      totalPrice: null,
    },
  },
  {
    id: "org-pharmacy-rosemont",
    name: "Rosemont Pharmacy",
    type: "specialty",
    city: "Rosemont",
    state: "PA",
    specialties: ["overnight-shipping", "injection-training", "cold-chain"],
    demo: {
      acceptsInsurance: true,
      delivery: "Free same-day or next-day shipping",
      totalPrice: null,
    },
  },
];

const MOCK_PHARMACY_MEDICATION_PRICE_MAP: Record<
  string,
  Partial<Record<string, number>>
> = {
  "org-pharmacy-freedom": {
    "demo-med-gonal-f": 980,
    "demo-med-cetrotide": 345,
    "demo-med-ovidrel": 160,
    "demo-med-endometrin": 190,
    "demo-med-dexamethasone": 32,
  },
  "org-pharmacy-mdr": {
    "demo-med-gonal-f": 1050,
    "demo-med-cetrotide": 385,
    "demo-med-ovidrel": 175,
    "demo-med-endometrin": 300,
    "demo-med-dexamethasone": 45,
  },
  "org-pharmacy-vfp": {
    "demo-med-gonal-f": 940,
    "demo-med-cetrotide": 370,
    "demo-med-ovidrel": 150,
    "demo-med-endometrin": 215,
    "demo-med-dexamethasone": 32,
  },
  "org-pharmacy-mandells": {
    "demo-med-gonal-f": 1030,
    "demo-med-cetrotide": 400,
    "demo-med-ovidrel": 170,
    "demo-med-endometrin": 310,
    "demo-med-dexamethasone": 46,
  },
  "org-pharmacy-cvs-specialty": {
    "demo-med-gonal-f": 1110,
    "demo-med-cetrotide": 420,
    "demo-med-ovidrel": 180,
    "demo-med-endometrin": 285,
    "demo-med-dexamethasone": 45,
  },
  "org-pharmacy-walgreens-specialty": {
    "demo-med-gonal-f": 1010,
    "demo-med-cetrotide": 390,
    "demo-med-ovidrel": 168,
    "demo-med-endometrin": 342,
    "demo-med-dexamethasone": 46,
  },
};

function buildDemoMedicationPrices(
  prices: Partial<Record<string, number>> = {},
): PharmacyDemoMedicationPrice[] {
  return MOCK_PHARMACY_MEDICATIONS.map((medication) => ({
    medicationId: medication.id,
    price: prices[medication.id] ?? null,
  }));
}

export const MOCK_PHARMACY_DIRECTORY: PharmacyDirectoryRecord[] =
  MOCK_PHARMACY_ROWS.map((row) => ({
    id: row.id,
    name: row.name,
    type: row.type,
    website: VERIFIED_CONTACTS[row.id]?.website,
    phone: VERIFIED_CONTACTS[row.id]?.phone,
    city: row.city,
    state: row.state,
    country: "US",
    specialties: row.specialties,
    source: "demo",
    sourceUrl: VERIFIED_CONTACTS[row.id]?.sourceUrl,
    quoteUrl: VERIFIED_CONTACTS[row.id]?.quoteUrl,
    medicationsUrl: VERIFIED_CONTACTS[row.id]?.medicationsUrl,
    quoteType: VERIFIED_CONTACTS[row.id]?.quoteType,
    demo: {
      ...row.demo,
      medicationPrices: buildDemoMedicationPrices(
        MOCK_PHARMACY_MEDICATION_PRICE_MAP[row.id],
      ),
    },
  }));

export const MOCK_PHARMACY_QUOTES: PharmacyDemoQuote[] = [
  {
    pharmacyId: "org-pharmacy-freedom",
    price: 1707,
    estimatedSavings: 1333,
  },
  {
    pharmacyId: "org-pharmacy-mdr",
    price: 1955,
    estimatedSavings: 1085,
  },
  {
    pharmacyId: "org-pharmacy-alto",
    price: null,
  },
];

/**
 * Public savings-program paths checked against program-owner pages on
 * 2026-07-26. These links are useful in live and demo views, but they do not
 * make an eligibility decision or provide a pharmacy quote.
 */
export const PHARMACY_SAVINGS_PROGRAMS: PharmacySavingsProgram[] = [
  {
    id: "emd-fertility-instant-savings",
    name: "Fertility Instant Savings",
    sponsor: "EMD Serono · Fertility LifeLines",
    headline:
      "Generate a cash-pay savings card for EMD Serono fertility medicines",
    processLabel:
      "Complete the savings form, generate the card, and present it to a participating pharmacy with your prescription.",
    eligibleMedIds: [
      "demo-med-gonal-f",
      "demo-med-cetrotide",
      "demo-med-ovidrel",
    ],
    medicationLabel: "Gonal-F, Cetrotide, and Ovidrel",
    savingsLabel: "Instant savings card",
    eligibilityLabel:
      "18+ with a valid U.S. prescription; self-pay only at participating pharmacies",
    sourceUrl: "https://www.fertilityinstantsavings.com/home.html",
    lastVerifiedOn: "2026-07-26",
    translations: {
      fr: {
        headline:
          "Génère une carte de remise cash pour les médicaments de fertilité EMD Serono",
        processLabel:
          "Remplis le formulaire, génère la carte et présente-la avec ton ordonnance dans une pharmacie partenaire.",
        medicationLabel: "Gonal-F, Cetrotide et Ovidrel",
        savingsLabel: "Carte de remise instantanée",
        eligibilityLabel:
          "18+, ordonnance américaine valide ; paiement cash uniquement dans les pharmacies partenaires",
      },
    },
  },
  {
    id: "emd-compassionate-care",
    name: "Compassionate Care",
    sponsor: "EMD Serono · Fertility LifeLines",
    headline:
      "Income-based and military savings for eligible self-pay patients",
    processLabel:
      "Apply through Fertility LifeLines with the required financial or military documentation, then reapply when the program period ends.",
    eligibleMedIds: [
      "demo-med-gonal-f",
      "demo-med-cetrotide",
      "demo-med-ovidrel",
    ],
    medicationLabel: "Gonal-F, Cetrotide, and Ovidrel",
    savingsLabel: "Up to 50% off",
    eligibilityLabel:
      "Qualifying income or military status; self-pay; reapply annually",
    sourceUrl:
      "https://www.emdseronofertility.com/content/healthcare-hcp/emd/en/fertility/patient/affording-treatment/medication-savings.html",
    lastVerifiedOn: "2026-07-26",
    translations: {
      fr: {
        headline:
          "Aides basées sur les revenus ou le statut militaire pour les patient·es éligibles sans couverture",
        processLabel:
          "Fais la demande via Fertility LifeLines avec les justificatifs financiers ou militaires requis, puis renouvelle-la à la fin de chaque période du programme.",
        medicationLabel: "Gonal-F, Cetrotide et Ovidrel",
        savingsLabel: "Jusqu'à 50 % de remise",
        eligibilityLabel:
          "Revenus éligibles ou statut militaire ; sans couverture assurance ; renouvellement annuel",
      },
    },
  },
  {
    id: "emd-compassionate-corps",
    name: "Compassionate Corps",
    sponsor: "EMD Serono · Fertility LifeLines",
    headline: "Free medication for eligible uninsured veterans and spouses",
    processLabel:
      "Confirm the veteran or spouse criteria with Fertility LifeLines, provide the required documentation, and follow the enrollment steps before filling.",
    eligibleMedIds: [
      "demo-med-gonal-f",
      "demo-med-cetrotide",
      "demo-med-ovidrel",
    ],
    medicationLabel: "Gonal-F, Cetrotide, and Ovidrel",
    savingsLabel: "Potentially $0 medication cost",
    eligibilityLabel:
      "Medically retired veteran or spouse; service-related infertility; no fertility medication coverage",
    sourceUrl:
      "https://www.emdseronofertility.com/content/healthcare-hcp/emd/en/fertility/patient/affording-treatment/medication-savings.html",
    lastVerifiedOn: "2026-07-26",
    translations: {
      fr: {
        headline:
          "Médicaments gratuits pour les vétéranes et vétérans éligibles sans assurance et leurs conjoint·es",
        processLabel:
          "Confirme les critères de vétéran·e ou conjoint·e auprès de Fertility LifeLines, fournis les justificatifs demandés et suis les étapes d'inscription avant de commander.",
        medicationLabel: "Gonal-F, Cetrotide et Ovidrel",
        savingsLabel: "Médicaments potentiellement à 0 $",
        eligibilityLabel:
          "Vétéran·e ou conjoint·e réformé·e pour raisons médicales ; infertilité liée au service ; pas de couverture pour les médicaments de fertilité",
      },
    },
  },
  {
    id: "emd-trumprx",
    name: "TrumpRx coupon",
    sponsor: "EMD Serono · TrumpRx",
    headline: "Official coupon for Gonal-F, Cetrotide, and Ovidrel",
    processLabel:
      "Open the offer, choose a participating pharmacy, and follow the checkout instructions before ordering.",
    eligibleMedIds: [
      "demo-med-gonal-f",
      "demo-med-cetrotide",
      "demo-med-ovidrel",
    ],
    medicationLabel: "Gonal-F, Cetrotide, and Ovidrel",
    savingsLabel: "Open official coupon",
    eligibilityLabel:
      "Out-of-pocket pricing; check your insurance copay before using a cash price",
    sourceUrl: "https://trumprx.gov/p/gonal-f",
    lastVerifiedOn: "2026-07-27",
    coupon: {
      medicationName: "Gonal-F, Cetrotide, and Ovidrel",
      bin: "610020",
      pcn: "PDMI",
      group: "99996218",
      memberId: "ERXEMDSERONO",
      sourceUrl: "https://trumprx.gov/p/gonal-f",
    },
    translations: {
      fr: {
        name: "Coupon TrumpRx",
        headline: "Coupon officiel pour Gonal-F, Cetrotide et Ovidrel",
        processLabel:
          "Ouvre l'offre, choisis une pharmacie partenaire et suis les consignes de commande avant de payer.",
        medicationLabel: "Gonal-F, Cetrotide et Ovidrel",
        savingsLabel: "Ouvrir le coupon officiel",
        eligibilityLabel:
          "Tarif hors assurance ; vérifie ton reste à charge avant d'opter pour un prix cash",
      },
    },
  },
  {
    id: "reunite-rx",
    name: "ReUnite Rx",
    sponsor: "Organon · ReUnite Rx",
    headline:
      "Reduced everyday cash pricing through fertility-specialty pharmacies",
    processLabel:
      "Ask a participating fertility-specialty pharmacy to check ReUnite pricing and enroll the prescription in the network.",
    eligibleMedIds: [],
    medicationLabel: "Follistim AQ, Ganirelix, and Pregnyl",
    savingsLabel: "Reduced everyday cash price",
    eligibilityLabel:
      "No fertility prescription coverage; participating network pharmacy; auto-enrollment",
    sourceUrl: "https://reuniterx.com/faq/",
    lastVerifiedOn: "2026-07-26",
    translations: {
      fr: {
        headline:
          "Prix cash réduits en permanence via les pharmacies spécialisées en fertilité",
        processLabel:
          "Demande à une pharmacie partenaire du réseau de vérifier les tarifs ReUnite et d'y inscrire ton ordonnance.",
        medicationLabel: "Follistim AQ, Ganirelix et Pregnyl",
        savingsLabel: "Prix cash réduit en permanence",
        eligibilityLabel:
          "Sans couverture pour les médicaments de fertilité ; pharmacie partenaire ; inscription automatique",
      },
    },
  },
  {
    id: "reunite-assist",
    name: "ReUnite Assist",
    sponsor: "ReUnite Rx",
    headline: "Additional need-based support for cash-pay Follistim AQ",
    processLabel:
      "Submit the prescription and current income documentation through a participating network pharmacy.",
    eligibleMedIds: [],
    medicationLabel: "Follistim AQ",
    savingsLabel: "Additional need-based discount",
    eligibilityLabel:
      "Cash-pay prescription; current income proof; participating network pharmacy",
    sourceUrl: "https://reuniterx.com/discount-programs/",
    lastVerifiedOn: "2026-07-26",
    translations: {
      fr: {
        headline:
          "Aide complémentaire selon les besoins pour Follistim AQ en cash-pay",
        processLabel:
          "Envoie ton ordonnance et tes justificatifs de revenus récents via une pharmacie partenaire du réseau.",
        medicationLabel: "Follistim AQ",
        savingsLabel: "Remise supplémentaire selon les besoins",
        eligibilityLabel:
          "Ordonnance cash-pay ; justificatifs de revenus récents ; pharmacie partenaire",
      },
    },
  },
  {
    id: "reunite-oncofertility",
    name: "ReUnite Oncofertility",
    sponsor: "ReUnite Rx",
    headline:
      "Oncology fertility-preservation support based on clinical and financial need",
    processLabel:
      "Have the oncology or fertility care team complete the required form, then submit it with the prescription and financial documentation.",
    eligibleMedIds: [],
    medicationLabel: "Follistim AQ, Ganirelix, and Pregnyl",
    savingsLabel: "Up to 100% of copay",
    eligibilityLabel:
      "Self-pay; fertility-threatening cancer treatment; no prior chemotherapy; oncologist form",
    sourceUrl: "https://reuniterx.com/discount-programs/",
    lastVerifiedOn: "2026-07-26",
    translations: {
      fr: {
        headline:
          "Aide à la préservation de la fertilité en oncologie, selon les besoins cliniques et financiers",
        processLabel:
          "Fais remplir le formulaire par l'équipe d'oncologie ou de fertilité, puis envoie-le avec l'ordonnance et les justificatifs financiers.",
        medicationLabel: "Follistim AQ, Ganirelix et Pregnyl",
        savingsLabel: "Jusqu'à 100 % du reste à charge",
        eligibilityLabel:
          "Cash-pay ; traitement anticancéreux menaçant la fertilité ; pas de chimiothérapie antérieure ; formulaire de l'oncologue",
      },
    },
  },
  {
    id: "ferring-heart-pathways",
    name: "Ferring HEART pathways",
    sponsor: "Ferring Fertility",
    headline:
      "HeartTomorrow, Heart for Heroes, Heart Beat, and HeartFelt support paths",
    processLabel:
      "Review the pathway that fits your situation, complete the relevant form with your care team, and submit it to Ferring for review.",
    eligibleMedIds: [],
    medicationLabel: "Ferring fertility medicines; confirm product eligibility",
    savingsLabel: "Program-specific assistance",
    eligibilityLabel:
      "Eligibility varies by program, diagnosis, insurance status, and treatment cycle",
    sourceUrl:
      "https://www.ferringfertility.com/healthcare-professionals/support-for-your-patients/",
    lastVerifiedOn: "2026-07-26",
    translations: {
      fr: {
        headline:
          "Parcours HeartTomorrow, Heart for Heroes, Heart Beat et HeartFelt",
        processLabel:
          "Regarde le parcours qui correspond à ta situation, remplis le formulaire avec ton équipe de soins et envoie-le à Ferring pour évaluation.",
        medicationLabel:
          "Médicaments de fertilité Ferring ; à vérifier selon le produit",
        savingsLabel: "Aide spécifique selon le programme",
        eligibilityLabel:
          "L'éligibilité varie selon le programme, le diagnostic, le statut assurance et le cycle",
      },
    },
  },
  {
    id: "livestrong-fertility",
    name: "Livestrong Fertility",
    sponsor: "Livestrong Foundation · EMD Serono partnership",
    headline:
      "For cancer patients: free EMD Serono stimulation meds and 25% off services at partner clinics",
    processLabel:
      "Apply through Livestrong Fertility before oncology treatment starts: document the diagnosis and treatment-related fertility risk. If approved, EMD Serono ships stimulation meds directly and a partner clinic bills the discounted rate.",
    eligibleMedIds: [
      "demo-med-gonal-f",
      "demo-med-cetrotide",
      "demo-med-ovidrel",
    ],
    medicationLabel:
      "EMD Serono fertility medicines (Gonal-F, Cetrotide, Ovidrel)",
    savingsLabel: "Free meds + 25% off clinic",
    eligibilityLabel:
      "Cancer diagnosed during reproductive years; treatment-related risk of infertility",
    sourceUrl: "https://livestrong.org/how-we-help/livestrong-fertility/",
    lastVerifiedOn: "2026-08-26",
    translations: {
      fr: {
        headline:
          "Pour les patient·es atteint·es d'un cancer : médicaments de stimulation EMD Serono offerts et 25 % de remise en clinique partenaire",
        processLabel:
          "Fais la demande auprès de Livestrong Fertility avant le début du traitement oncologique : documente le diagnostic et le risque d'infertilité lié au traitement. Une fois approuvé, EMD Serono envoie les médicaments de stimulation directement et la clinique partenaire applique le tarif réduit.",
        medicationLabel:
          "Médicaments de fertilité EMD Serono (Gonal-F, Cetrotide, Ovidrel)",
        savingsLabel: "Médicaments offerts + 25 % en clinique",
        eligibilityLabel:
          "Cancer diagnostiqué en âge de procréer ; risque d'infertilité lié au traitement",
      },
    },
  },
  {
    id: "inside-rx-fertility",
    name: "Inside Rx fertility card",
    sponsor: "Inside Rx · Fertility pharmacy network",
    headline:
      "Free cash card usable at Freedom, Village, and Apri. Best for non-EMD injectables (Follistim, Menopur)",
    processLabel:
      "Download the Inside Rx card (no sign-up), send your prescription to a participating fertility specialty pharmacy, present the card at checkout. For EMD Serono molecules (Gonal-F, Cetrotide, Ovidrel), check TrumpRx pricing first. It often beats Inside Rx at the same pharmacies.",
    eligibleMedIds: [],
    medicationLabel:
      "Featured brand and generic fertility medicines at participating pharmacies",
    savingsLabel: "Up to 80% off eligible meds",
    eligibilityLabel:
      "Cash pay only; not usable with Medicare, Medicaid, or TRICARE benefits",
    sourceUrl:
      "https://insiderx.com/pharmacy-near-me/freedomfertility/discount-card",
    lastVerifiedOn: "2026-08-26",
    translations: {
      fr: {
        name: "Carte fertilité Inside Rx",
        headline:
          "Carte cash gratuite acceptée chez Freedom, Village et Apri, idéale pour les injectables hors EMD (Follistim, Menopur)",
        processLabel:
          "Télécharge la carte Inside Rx (sans inscription), envoie ton ordonnance dans une pharmacie spécialisée partenaire et présente la carte au paiement. Pour les molécules EMD Serono (Gonal-F, Cetrotide, Ovidrel), vérifie d'abord les tarifs TrumpRx : ils battent souvent Inside Rx dans les mêmes pharmacies.",
        medicationLabel:
          "Médicaments de fertilité (marques et génériques) dans les pharmacies partenaires",
        savingsLabel: "Jusqu'à 80 % de remise",
        eligibilityLabel:
          "Cash-pay uniquement ; non utilisable avec Medicare, Medicaid ou TRICARE",
      },
    },
  },
];

function getDirectoryType(organization: Organization): PharmacyDirectoryType {
  const specialties = organization.specialties ?? [];
  if (specialties.includes("retail")) return "retail";
  if (specialties.includes("mail-order")) return "mail_order";
  return "specialty";
}

export function toPharmacyDirectoryRecord(
  organization: Organization,
  source: PharmacyDirectorySource,
): PharmacyDirectoryRecord {
  const verified =
    source === "curated" ? VERIFIED_CONTACTS[organization.id] : undefined;

  return {
    id: organization.id,
    name: organization.name,
    type: getDirectoryType(organization),
    website: verified?.website ?? organization.website,
    phone:
      verified?.phone ?? (source === "live" ? organization.phone : undefined),
    city: organization.address.city || undefined,
    state: organization.address.state || undefined,
    country: organization.address.country || undefined,
    specialties: organization.specialties ?? [],
    description: organization.description,
    source,
    sourceUrl: verified?.sourceUrl ?? organization.website,
    quoteUrl: verified?.quoteUrl,
    medicationsUrl: verified?.medicationsUrl,
    quoteType: verified?.quoteType,
    compoundingOnSite: organization.pharmacyDetails?.compoundingOnSite,
    delivery: organization.pharmacyDetails?.delivery,
    publishedPrices: organization.pharmacyDetails?.publishedPrices,
    organization,
  };
}
